import { useRouter } from "next/router";
import { useAppDispatch, useAppSelector } from "../redux";
import { dashboardActions } from "@/redux/stores/dashboardStore";
import { createContext, useCallback, useEffect, useMemo } from "react";
import { getDashBoardState } from "@/redux/store";
import { useGetUserInfoQuery } from "@/redux/api/userApi";
import { FolderPageContextType } from "@/types/dashboard.api";
import CryptoJS from 'crypto-js';

export function useDashboardPageLoading() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  
  useEffect(() => {

    const handlePageStart = () => dispatch(dashboardActions.setPageLoading(true));
    const handlePageComplete = () => dispatch(dashboardActions.setPageLoading(false));
    const handlePageError = () => dispatch(dashboardActions.setPageLoading(false));

    if(!router) return;
    router.events.on('routeChangeStart', handlePageStart);
    router.events.on('routeChangeComplete', handlePageComplete);
    router.events.on('routeChangeError', handlePageError);

    return() => {
      router.events.off('routeChangeStart', handlePageStart);
      router.events.off('routeChangeComplete', handlePageComplete);
      router.events.off('routeChangeError', handlePageError)
    }
  }, [dispatch, router])

  return {}
}

export function useUserWorkspace() {

  const { accountUserInfo, workspaceList, selectedWorkspaceIndex } = useAppSelector(store => getDashBoardState(store) ?? false);

  const currentWorkspace = useMemo(() => {
    return workspaceList[selectedWorkspaceIndex] || { workspaceId: accountUserInfo?.workspaceId, isPrivate: true }
  }, [accountUserInfo?.workspaceId, selectedWorkspaceIndex, workspaceList]);

  const currentUserPlan = useMemo(() => {
    if(accountUserInfo?.plan) {
      return accountUserInfo.plan.toLocaleLowerCase();
    } else {
      return 'none';
    }
  }, [accountUserInfo?.plan]);

  const isPremiumUser = useMemo(() => {
    const premiumPlan = ['team', 'pro', 'enterprise'];
    return premiumPlan.some(plan => currentUserPlan.includes(plan) || accountUserInfo?.enterprise);
  }, [accountUserInfo?.enterprise, currentUserPlan]);

  const getWorkspaceParams = useCallback((workspaceId: string, projectOwner: string) => {
    return CryptoJS.AES.encrypt(
      JSON.stringify([
        { workspaceId },
        { projectOwner }
      ]), 'workspace').toString()
  }, [])

  return { 
    currentWorkspace,
    currentUserPlan,
    isPremiumUser,
    userInfo: accountUserInfo,
    getWorkspaceParams,
  }
}

export const FolderPageContext = createContext<FolderPageContextType | null>(
  null
);
